import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Mail, Phone, MapPin, Calendar, Clock, User, Briefcase, RefreshCcw, Bell } from 'lucide-react';
import { getContacts, getQuotes, getBookings, getApplications } from '../api/client';
import { toast } from 'sonner';

const AdminPage = () => {
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);
  const [newSubmissions, setNewSubmissions] = useState({
    contacts: 0,
    quotes: 0,
    bookings: 0,
    applications: 0
  });

  useEffect(() => {
    loadAllData();
    // Get last checked time from localStorage
    const lastCheck = localStorage.getItem('adminLastChecked');
    if (lastCheck) {
      setLastChecked(new Date(lastCheck));
    }
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [contactsData, quotesData, bookingsData, applicationsData] = await Promise.all([
        getContacts(),
        getQuotes(),
        getBookings(),
        getApplications()
      ]);
      setContacts(contactsData);
      setQuotes(quotesData);
      setBookings(bookingsData);
      setApplications(applicationsData);
      
      // Calculate new submissions since last check
      const lastCheck = localStorage.getItem('adminLastChecked');
      if (lastCheck) {
        const lastCheckDate = new Date(lastCheck);
        const newCounts = {
          contacts: contactsData.filter(item => new Date(item.created_at) > lastCheckDate).length,
          quotes: quotesData.filter(item => new Date(item.created_at) > lastCheckDate).length,
          bookings: bookingsData.filter(item => new Date(item.created_at) > lastCheckDate).length,
          applications: applicationsData.filter(item => new Date(item.created_at) > lastCheckDate).length
        };
        setNewSubmissions(newCounts);
        
        const totalNew = Object.values(newCounts).reduce((a, b) => a + b, 0);
        if (totalNew > 0) {
          toast.success(`${totalNew} new submission${totalNew > 1 ? 's' : ''} since last visit!`);
        }
      }
      
      // Update last checked time
      const now = new Date().toISOString();
      localStorage.setItem('adminLastChecked', now);
      setLastChecked(new Date(now));
      
    } catch (error) {
      console.error('Error loading admin data:', error);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadAllData();
    toast.success('Dashboard refreshed!');
  };

  const isNew = (createdAt) => {
    if (!lastChecked) return false;
    const itemDate = new Date(createdAt);
    const checkDate = new Date(localStorage.getItem('adminLastChecked'));
    return itemDate > checkDate;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-black text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-2">
                Admin <span className="text-red-600">Dashboard</span>
              </h1>
              <p className="text-gray-300">View and manage all form submissions</p>
              {lastChecked && (
                <p className="text-sm text-gray-400 mt-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Last checked: {formatDate(lastChecked.toISOString())}
                </p>
              )}
            </div>
            <Button 
              onClick={handleRefresh} 
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-black"
            >
              <RefreshCcw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2">
                    <p className="text-3xl font-bold text-red-600">{contacts.length}</p>
                    {newSubmissions.contacts > 0 && (
                      <Badge variant="destructive" className="bg-red-600">
                        <Bell className="w-3 h-3 mr-1" />
                        {newSubmissions.contacts} new
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Contact Forms</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2">
                    <p className="text-3xl font-bold text-red-600">{quotes.length}</p>
                    {newSubmissions.quotes > 0 && (
                      <Badge variant="destructive" className="bg-red-600">
                        <Bell className="w-3 h-3 mr-1" />
                        {newSubmissions.quotes} new
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Quote Requests</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2">
                    <p className="text-3xl font-bold text-red-600">{bookings.length}</p>
                    {newSubmissions.bookings > 0 && (
                      <Badge variant="destructive" className="bg-red-600">
                        <Bell className="w-3 h-3 mr-1" />
                        {newSubmissions.bookings} new
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Bookings</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2">
                    <p className="text-3xl font-bold text-red-600">{applications.length}</p>
                    {newSubmissions.applications > 0 && (
                      <Badge variant="destructive" className="bg-red-600">
                        <Bell className="w-3 h-3 mr-1" />
                        {newSubmissions.applications} new
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Job Applications</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Submissions */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="contacts">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="contacts">Contacts ({contacts.length})</TabsTrigger>
              <TabsTrigger value="quotes">Quotes ({quotes.length})</TabsTrigger>
              <TabsTrigger value="bookings">Bookings ({bookings.length})</TabsTrigger>
              <TabsTrigger value="applications">Applications ({applications.length})</TabsTrigger>
            </TabsList>

            {/* Contact Forms */}
            <TabsContent value="contacts" className="space-y-4 mt-6">
              {contacts.length === 0 ? (
                <Card>
                  <CardContent className="pt-6 text-center text-gray-500">
                    No contact form submissions yet
                  </CardContent>
                </Card>
              ) : (
                contacts.map((contact) => (
                  <Card key={contact._id}>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <User className="w-5 h-5" />
                            {contact.name}
                          </CardTitle>
                          <CardDescription>{formatDate(contact.created_at)}</CardDescription>
                        </div>
                        <Badge variant="secondary">Contact</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="w-4 h-4 text-red-600" />
                        <a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline">
                          {contact.email}
                        </a>
                      </div>
                      {contact.phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-red-600" />
                          <a href={`tel:${contact.phone}`}>{contact.phone}</a>
                        </div>
                      )}
                      <div className="mt-3 p-3 bg-gray-50 rounded-md">
                        <p className="text-sm font-semibold mb-1">Message:</p>
                        <p className="text-sm text-gray-700">{contact.message}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>

            {/* Quote Requests */}
            <TabsContent value="quotes" className="space-y-4 mt-6">
              {quotes.length === 0 ? (
                <Card>
                  <CardContent className="pt-6 text-center text-gray-500">
                    No quote requests yet
                  </CardContent>
                </Card>
              ) : (
                quotes.map((quote) => (
                  <Card key={quote._id}>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <User className="w-5 h-5" />
                            {quote.name}
                          </CardTitle>
                          <CardDescription>{formatDate(quote.created_at)}</CardDescription>
                        </div>
                        <Badge variant="secondary">Quote</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-red-600" />
                          <a href={`mailto:${quote.email}`} className="text-blue-600 hover:underline">
                            {quote.email}
                          </a>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-red-600" />
                          <a href={`tel:${quote.phone}`}>{quote.phone}</a>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                        <div className="bg-gray-50 p-3 rounded-md">
                          <p className="text-xs text-gray-600">Service Type</p>
                          <p className="text-sm font-semibold capitalize">{quote.serviceType}</p>
                        </div>
                        {quote.propertyType && (
                          <div className="bg-gray-50 p-3 rounded-md">
                            <p className="text-xs text-gray-600">Property Type</p>
                            <p className="text-sm font-semibold capitalize">{quote.propertyType}</p>
                          </div>
                        )}
                        {quote.squareFootage && (
                          <div className="bg-gray-50 p-3 rounded-md">
                            <p className="text-xs text-gray-600">Square Footage</p>
                            <p className="text-sm font-semibold">{quote.squareFootage} sq ft</p>
                          </div>
                        )}
                      </div>
                      {quote.message && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-md">
                          <p className="text-sm font-semibold mb-1">Additional Details:</p>
                          <p className="text-sm text-gray-700">{quote.message}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>

            {/* Bookings */}
            <TabsContent value="bookings" className="space-y-4 mt-6">
              {bookings.length === 0 ? (
                <Card>
                  <CardContent className="pt-6 text-center text-gray-500">
                    No bookings yet
                  </CardContent>
                </Card>
              ) : (
                bookings.map((booking) => (
                  <Card key={booking._id}>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <User className="w-5 h-5" />
                            {booking.name}
                          </CardTitle>
                          <CardDescription>{formatDate(booking.created_at)}</CardDescription>
                        </div>
                        <Badge variant="secondary">Booking</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-red-600" />
                          <a href={`mailto:${booking.email}`} className="text-blue-600 hover:underline">
                            {booking.email}
                          </a>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-red-600" />
                          <a href={`tel:${booking.phone}`}>{booking.phone}</a>
                        </div>
                      </div>
                      <div className="flex items-start gap-2 text-sm">
                        <MapPin className="w-4 h-4 text-red-600 mt-0.5" />
                        <span>{booking.address}</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                        <div className="bg-gray-50 p-3 rounded-md">
                          <p className="text-xs text-gray-600">Service Type</p>
                          <p className="text-sm font-semibold capitalize">{booking.serviceType}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-md">
                          <p className="text-xs text-gray-600">Property Type</p>
                          <p className="text-sm font-semibold capitalize">{booking.propertyType}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-md">
                          <p className="text-xs text-gray-600 flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> Preferred Date
                          </p>
                          <p className="text-sm font-semibold">{booking.date}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-md">
                          <p className="text-xs text-gray-600 flex items-center gap-1">
                            <Clock className="w-3 h-3" /> Time Slot
                          </p>
                          <p className="text-sm font-semibold">{booking.timeSlot}</p>
                        </div>
                      </div>
                      {booking.specialInstructions && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-md">
                          <p className="text-sm font-semibold mb-1">Special Instructions:</p>
                          <p className="text-sm text-gray-700">{booking.specialInstructions}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>

            {/* Job Applications */}
            <TabsContent value="applications" className="space-y-4 mt-6">
              {applications.length === 0 ? (
                <Card>
                  <CardContent className="pt-6 text-center text-gray-500">
                    No job applications yet
                  </CardContent>
                </Card>
              ) : (
                applications.map((app) => (
                  <Card key={app._id}>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <Briefcase className="w-5 h-5" />
                            {app.firstName} {app.lastName}
                          </CardTitle>
                          <CardDescription>{formatDate(app.created_at)}</CardDescription>
                        </div>
                        <Badge variant="secondary">Application</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-red-600" />
                          <a href={`mailto:${app.email}`} className="text-blue-600 hover:underline">
                            {app.email}
                          </a>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-red-600" />
                          <a href={`tel:${app.phone}`}>{app.phone}</a>
                        </div>
                      </div>
                      <div className="flex items-start gap-2 text-sm">
                        <MapPin className="w-4 h-4 text-red-600 mt-0.5" />
                        <span>{app.address}</span>
                      </div>
                      {app.resume_filename && (
                        <div className="bg-blue-50 p-3 rounded-md">
                          <p className="text-sm font-semibold text-blue-900">
                            📎 Resume Attached: {app.resume_filename}
                          </p>
                        </div>
                      )}
                      {app.experience && (
                        <div className="p-3 bg-gray-50 rounded-md">
                          <p className="text-sm font-semibold mb-1">Experience:</p>
                          <p className="text-sm text-gray-700">{app.experience}</p>
                        </div>
                      )}
                      <div className="p-3 bg-gray-50 rounded-md">
                        <p className="text-sm font-semibold mb-1">Availability:</p>
                        <p className="text-sm text-gray-700">{app.availability}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-md">
                        <p className="text-sm font-semibold mb-1">Why Join Taylormade:</p>
                        <p className="text-sm text-gray-700">{app.whyJoin}</p>
                      </div>
                      {app.references && (
                        <div className="p-3 bg-gray-50 rounded-md">
                          <p className="text-sm font-semibold mb-1">References:</p>
                          <p className="text-sm text-gray-700">{app.references}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default AdminPage;
